/**
 * Encounter Adapter
 *
 * Transforms Encounter data into UnifiedEncounterItem format
 * for use in the EncounterList component (connected mode).
 */

import { Encounter } from '@/types/encounter';
import { UnifiedEncounterItem, EncounterStatus, MatchStatus, RecordingStatus, TranscriptionStatus } from '../types';

/**
 * Get patient full name from encounter patient data
 */
function getPatientName(patientData: Encounter['patientData']): string {
  const firstName = patientData.firstName || '';
  const lastName = patientData.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || 'Unknown Patient';
}

/**
 * Map encounter status string to EncounterStatus type
 */
function mapEncounterStatus(status: string): EncounterStatus {
  const statusMap: Record<string, EncounterStatus> = {
    scheduled: 'scheduled',
    'in-progress': 'in-progress',
    completed: 'completed',
    cancelled: 'cancelled',
  };

  return statusMap[status] || 'scheduled';
}

/**
 * Collapse the newest recording's status into the list's recording and transcription statuses.
 * No recording, or one that never got its audio, reads as pending on both.
 */
function mapRecordingStatuses(latest: string | null | undefined): {
  recording: RecordingStatus;
  transcription: TranscriptionStatus;
} {
  switch (latest) {
    case 'uploaded':
    case 'processing':
      return { recording: 'completed', transcription: 'processing' };
    case 'transcribed':
      return { recording: 'completed', transcription: 'completed' };
    case 'transcription_failed':
      return { recording: 'completed', transcription: 'failed' };
    case 'failed':
      return { recording: 'failed', transcription: 'failed' };
    default:
      return { recording: 'pending', transcription: 'pending' };
  }
}

/**
 * Transform a single Encounter into UnifiedEncounterItem
 */
export function adaptEncounter(encounter: Encounter): UnifiedEncounterItem {
  // The list endpoint sends displayStatus; other sources only carry the detail status.
  const status = encounter.displayStatus ?? mapEncounterStatus(encounter.encounterDetails.status);
  const statuses = mapRecordingStatuses(encounter.latestRecordingStatus);

  return {
    id: encounter.id,
    patientName: getPatientName(encounter.patientData),
    patientMrn: encounter.patientData.mrn,
    patientId: undefined, // External patients don't have internal IDs
    scheduledDate: new Date(encounter.encounterDetails.scheduledDate),
    type: encounter.encounterDetails.type,
    location: encounter.encounterDetails.location,
    chiefComplaint: encounter.encounterDetails.chiefComplaint,
    providerName: encounter.encounterDetails.providerName,
    providerId: encounter.assignedUserId,
    status,
    recordingStatus: statuses.recording,
    transcriptionStatus: statuses.transcription,
    matchStatus: encounter.matchStatus as MatchStatus,
    externalSystem: encounter.externalSystemId,
    externalId: encounter.externalEncounterId,
    hasTranscript: statuses.transcription === 'completed',
    canStartRecording: statuses.recording !== 'completed' && encounter.matchStatus === 'matched',
    // The inline fast-speech path holds a recording at 'uploaded' for the whole transcription
    // and never reaches 'processing', so both count as in flight.
    isTranscribing: encounter.latestRecordingStatus === 'uploaded' || encounter.latestRecordingStatus === 'processing',
    _raw: encounter,
    _type: 'encounter',
  };
}

/**
 * Transform an array of Encounters into UnifiedEncounterItems
 */
export function adaptEncounters(encounters: Encounter[]): UnifiedEncounterItem[] {
  return encounters.map(adaptEncounter);
}

export default adaptEncounter;
